import { useState, useEffect, useContext, useMemo } from "react";
import List from "@mui/material/List";
import ListItem from "./ListItem";
import ListItemText from "@mui/material/ListItemText";
import {
    Typography,
    ListItemButton,
    Box,
    IconButton,
    Tooltip,
} from "@mui/material";
import config from "../config";
import { useMap } from "@mapcomponents/react-maplibre";
import { DataContext } from "../context/DataContext";
import Downloader from "./Downloader";
import ResultInfo from "./ResultInfo/ResultInfo";
import WarningIcon from "@mui/icons-material/Warning";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const standardErrorMessage = "Fehler bei der Überprüfung.";
const backgroundColor = "rgba(55,55,55,0.1)";

export default function FeatureInfo(props) {
    const mapHook = useMap({ mapId: "map_1" });
    const dataContext = useContext(DataContext);

    const {
        selectedFromList,
        selectedFeatures,
        setSelectedArea,
        setSelectedCenter,
    } = dataContext;

    // Protected Areas
    const [matchingProtectedAreas, setMatchingProtectedAreas] = useState();
    const [protectedAreasisLoading, setprotectedAreasIsLoading] =
        useState(true);
    const [expanded, setExpanded] = useState(null);
    const [description, setDescription] = useState();

    // Glöz2 Status
    const [matchingMoorArea, setMatchingMoorArea] = useState();
    const [moorAreaisLoading, setMoorAreasIsLoading] = useState(true);
    const [glözExpanded, setGlözExpanded] = useState(false);

    // NUT Areas
    const [matchingNUTArea, setMatchingNutArea] = useState();
    const [NUTAreaisLoading, setNUTAreaIsLoading] = useState(true);
    const [NUTExpanded, setNUTExpanded] = useState(false);

    const handleAccordionChange = (id) => {
        setExpanded((prev) => (prev === id ? null : id));
    };

    const info = useMemo(() => {
        const feature = dataContext.selectedFeatures.find(
            (el) => el.id === dataContext.selectedFromList,
        );

        if (!feature?.properties) return {};
        return feature.properties;
    }, [dataContext.selectedFromList, dataContext.selectedFeatures]);

    useEffect(() => {
        if (!selectedFromList) return;

        const selectedFeature = selectedFeatures.find(
            (el) => el.id === selectedFromList,
        );
        const fid = selectedFeature?.properties?.feldblockidentnummer;
        if (!fid) return;

        // get Feld Area
        setSelectedArea("wird berechnet ...");
        setDescription("");

        fetch(
            config.backendOrigin +
                "api/fid/area/" +
                "?fid=" +
                fid,
            {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            },
        )
            .then((res) => {
                if (!res.ok) {
                    return false;
                } else {
                    return res.json();
                }
            })
            .then((data) => {
                if (data.area_sqm) {
                    setSelectedArea(
                        (data.area_sqm * 0.0001).toFixed(2) + " ha",
                    );
                    if (data.description) {
                        setDescription(data.description);
                    }
                } else {
                    setSelectedArea(standardErrorMessage);
                }
                if (data.center) {
                    setSelectedCenter(data.center.coordinates);
                }
            })
            .catch((error) => {
                console.error("Error:", error);
            });

        // GET Protected Areas
        setprotectedAreasIsLoading(true);
        setExpanded(null);
        setMatchingProtectedAreas("wird geprüft...");

        fetch(
            config.backendOrigin +
                "api/protected/" +
                fid,
            {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            },
        )
            .then((res) => {
                if (!res.ok) {
                    return false;
                } else {
                    return res.json();
                }
            })
            .then((data) => {
                setMatchingProtectedAreas(data.result);
                 dataContext.setSelectedFeatureMatches((current) => {
                      

                    if (!current) {
                        return { "protectedAreas":data.result };
                    }
                    const newState = current;
                    current.protectedAreas = data.result;
                    return newState;
                });

                setprotectedAreasIsLoading(false);
            })
            .catch((error) => {
                setprotectedAreasIsLoading(true);

                setMatchingProtectedAreas(standardErrorMessage);

                console.error("Error:", error);
            });

        // GET Moor Area (GLÖZ2)
        setMoorAreasIsLoading(true);
        setMatchingMoorArea("wird geprüft...");

        fetch(
            config.backendOrigin +
                "api/moor_flaeche/" +
                fid,
            {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            },
        )
            .then((res) => {
                if (!res.ok) {
                    return false;
                } else {
                    return res.json();
                }
            })
            .then((data) => {
                setMatchingMoorArea(data.result);
                setMoorAreasIsLoading(false);
                dataContext.setSelectedFeatureMatches((current) => {
                    if (!current) {
                        return { matchingMoorArea: data.result };
                    }

                    const newState = current;
                    current.matchingMoorArea = data.result;
                    return newState;
                });
            })
            .catch((error) => {
                setMoorAreasIsLoading(true);

                setMatchingMoorArea(standardErrorMessage);

                console.error("Error:", error);
            });

        // GET Nut2 Infos
        setNUTAreaIsLoading(true);
        setMatchingNutArea("wird geprüft...");

        fetch(
            config.backendOrigin +
                "api/nut2/" +
                fid,
            {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            },
        )
            .then((res) => {
                if (!res.ok) {
                    return false;
                } else {
                    return res.json();
                }
            })
            .then((data) => {
                if (!Array.isArray(data?.result)) {
                    setMatchingNutArea(standardErrorMessage);
                    setNUTAreaIsLoading(false);
                    return;
                }

                setMatchingNutArea(data.result);
                dataContext.setSelectedFeatureMatches((current) => {
                    const areasList = [];
                    data.result.forEach((area) =>
                        areasList.push(area.nuts_code),
                    );

                    if (!current) {
                        return { NUT2: areasList.join(", ") };
                    }
                    const newState = current;
                    current.NUT2 = areasList.join(", ");
                    return newState;
                });
                setNUTAreaIsLoading(false);
            })
            .catch((error) => {
                setNUTAreaIsLoading(true);
                setMatchingNutArea(standardErrorMessage);
                console.error("Error:", error);
            });
    }, [
        selectedFromList,
        selectedFeatures,
        setSelectedArea,
        setSelectedCenter,
    ]); // Now only depend on the specific properties

    const hoverPreview = (feature) => {
        mapHook.map.setFeatureState(
            {
                sourceLayer: "sql_statement",
                source: "flik-data",
                id: feature.gid,
            },
            { hover: true },
        );
    };

    const hoverLeave = (feature) => {
        mapHook.map.setFeatureState(
            {
                sourceLayer: "sql_statement",
                source: "flik-data",
                id: feature.gid,
            },
            { hover: false },
        );
    };

    return (
        <>
            {props.feature && (
                <List
                    disablePadding={true}
                    sx={{
                        width: "90%",
                        backgroundColor: backgroundColor,
                        margin: "0px auto",
                        padding: "5px 0px",
                    }}
                >
                    <Typography
                        sx={{
                            marginBottom: "5px",
                        }}
                    >
                        Ausgewählte Flächen: {props.feature.length}
                    </Typography>
                    <Box
                        sx={{
                            maxHeight: "210px",
                            overflow: "auto",
                            marginBottom: "5px",
                            padding: "20px auto",
                        }}
                    >
                        {props.feature.map((feature) => (
                            <>
                                {feature?.properties?.feldblockidentnummer && (
                                    <ListItemButton
                                        key={feature.id}
                                        sx={{
                                            backgroundColor:
                                                dataContext.selectedFromList ===
                                                feature.id
                                                    ? "rgba(99,99,99,1)"
                                                    : "rgba(33,33,33,1)",
                                            padding: "4px 15px",
                                            marginBottom: "5px",
                                            borderRadius: "20px",
                                        }}
                                        onClick={() => {
                                            dataContext.setSelectedFromList(
                                                feature.id,
                                            );
                                        }}
                                        onMouseOver={() =>
                                            hoverPreview(feature.properties)
                                        }
                                        onMouseLeave={() =>
                                            hoverLeave(feature.properties)
                                        }
                                    >
                                        <Typography>
                                            {
                                                feature.properties
                                                    .feldblockidentnummer
                                            }
                                        </Typography>
                                    </ListItemButton>
                                )}
                            </>
                        ))}
                    </Box>

                    {info && (
                        <Box
                            sx={{
                                overflowY: props.isMobil ? "hidden" : "auto",
                                overflowX: "hidden",
                                flex: "1 1 auto",
                                paddingRight: 1,
                                display: "flex",
                                flexDirection: "column",
                                maxHeight: props.isMobil ? "auto" : "68vh",
                                scrollbarColor:
                                    "rgba(99,99,99,1)" + backgroundColor,
                            }}
                        >
                            <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            FLIK-Nummer
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="body2">
                                            {info.feldblockidentnummer}
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            Bodennutzungsart
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="body2">
                                            {info.bodennutzungstyp}{" "}
                                            {dataContext.glossar?.[info.land]?.[
                                                info.bodennutzungstyp
                                            ]
                                                ? "(" +
                                                  dataContext.glossar?.[
                                                      info.land
                                                  ]?.[info.bodennutzungstyp] +
                                                  ")"
                                                : info.bodennutzungstyp
                                                  ? info.bodennutzungstyp
                                                  : "Nicht bekannt"}
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {dataContext.selectedCenter && (
                                <ListItem divider={true}>
                                    <ListItemText
                                        primary={
                                            <Typography variant="h6">
                                                Referenzkoordinaten
                                            </Typography>
                                        }
                                        secondary={
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    color: "white",
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    {dataContext.selectedCenter[1].toFixed(
                                                        5,
                                                    )}
                                                    ,{" "}
                                                    {dataContext.selectedCenter[0].toFixed(
                                                        5,
                                                    )}
                                                </Typography>
                                                <Tooltip title="Koordinaten kopieren">
                                                    <IconButton
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(
                                                                `${dataContext.selectedCenter[1]} ${dataContext.selectedCenter[0]}`,
                                                            );
                                                        }}
                                                        size="small"
                                                        color="secondary"
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            )}

                            {(info.status === "unklar" ||
                                info.status === "mehrfach_typ") && (
                                <ListItem divider={true}>
                                    <Typography
                                        variant="caption"
                                        color="#e3c840"
                                    >
                                        {info.status === "unklar"
                                            ? "Die Fläche wird nur mit zusätzlichen Auflagen genutzt werden können"
                                            : info.status === "unbekannt"
                                              ? "Die Fläche ist keine bestimmte Bodennutzungsart zugeordnet. Eine genaue Klarstellung ist erforderlich"
                                              : "Die Fläche ist mit unterschiedlichen Bodennutzungen registriert. Eine genaue Klarstellung ist erforderlich."}
                                    </Typography>
                                    <WarningIcon htmlColor="#e3c840" />
                                </ListItem>
                            )}

                            <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            Flächengröße
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="body2">
                                            {dataContext.selectedArea}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            Jahr
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="body2">
                                            {info.jahr}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            Land
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="body2">
                                            {info.land}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            {description ? (
                                <ListItem divider={true}>
                                    <ListItemText
                                        primary={
                                            <Typography variant="h6">
                                                {" "}
                                                Bemerkung{" "}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="body2">
                                                {description}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ) : (
                                ""
                            )}
                            {info.flaeche && (
                                <ListItem divider={true}>
                                    <ListItemText
                                        primary={
                                            <Typography variant="h6">
                                                {" "}
                                                Größe{" "}
                                            </Typography>
                                        }
                                        secondary={info.flaeche}
                                    />
                                </ListItem>
                            )}
                            {/* <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            {" "}
                                            gid{" "}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="body2">
                                            {info.gid}
                                        </Typography>
                                    }
                                />
                            </ListItem> */}
                            <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            {" "}
                                            Schutzgebiete{" "}
                                        </Typography>
                                    }
                                    secondary={
                                        <ResultInfo
                                            isLoading={protectedAreasisLoading}
                                            expanded={expanded}
                                            matchingAreas={
                                                matchingProtectedAreas
                                            }
                                            onChange={handleAccordionChange}
                                            type="protectedAreas"
                                        />
                                    }
                                />
                            </ListItem>
                            <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            Organische Böden
                                        </Typography>
                                    }
                                    secondary={
                                        <ResultInfo
                                            isLoading={moorAreaisLoading}
                                            expanded={glözExpanded}
                                            matchingAreas={matchingMoorArea}
                                            onChange={(id) => {
                                                setGlözExpanded((prev) =>
                                                    prev === id ? null : id,
                                                );
                                            }}
                                            type="glöz2"
                                        />
                                    }
                                />
                            </ListItem>
                            <ListItem divider={true}>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            NUT2 Gebiet(e)
                                        </Typography>
                                    }
                                    secondary={
                                        <ResultInfo
                                            isLoading={NUTAreaisLoading}
                                            expanded={NUTExpanded}
                                            matchingAreas={matchingNUTArea}
                                            onChange={(id) => {
                                                setNUTExpanded((prev) =>
                                                    prev === id ? null : id,
                                                );
                                            }}
                                            type="nut2"
                                        />
                                    }
                                />
                            </ListItem>
                        </Box>
                    )}
                    <Downloader features={props.feature} />
                </List>
            )}
        </>
    );
}
